<script lang="ts">
  import type { FrameDetection } from '@/lib/onnxInference';
  import { filterInterestingFrames } from '@/lib/onnxInference';
  import { brandStore } from '@/lib/brandStore.svelte';
  import { session } from '@/lib/session.svelte';
  import { navigateTo } from '@/lib/router';
  import DetectedFrameCard from './DetectedFrameCard.svelte';

  const detections = $derived<FrameDetection[]>(session.videoDetections ?? []);
  const interesting = $derived(filterInterestingFrames(detections, 0.35));
  const duration = $derived(interesting.length > 0 ? Math.max(...interesting.map(d => d.time)) : 0);

  const uniqueParts = $derived.by(() => {
    const seen = new Set<string>();
    return detections.filter(d => {
      if (!d.className || d.className === 'outro') return false;
      if (seen.has(d.className)) return false;
      seen.add(d.className);
      return true;
    });
  });

  let videoEl = $state<HTMLVideoElement | null>(null);
  let currentTime = $state(0);
  let selectedDetection = $state<FrameDetection | null>(null);

  const videoSrc = $derived(session.videoUrl ?? '');

  function formatDur(s: number): string {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }

  function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }

  function markerPercent(d: FrameDetection): number {
    if (!duration) return 0;
    return (d.time / duration) * 100;
  }

  function jumpTo(d: FrameDetection) {
    currentTime = d.time;
    if (videoEl) videoEl.currentTime = d.time;
  }

  function openModal(d: FrameDetection) {
    selectedDetection = d;
  }

  function closeModal() {
    selectedDetection = null;
  }

  function goToPart() {
    if (!selectedDetection) return;
    const d = selectedDetection;
    session.setResults({
      category: d.className ?? 'outro',
      confidence: d.confidence,
      scores: d.allScores as Record<string, number>,
      matches: [],
    });
    selectedDetection = null;
    navigateTo('results');
  }

  function goAnalyzeAgain() {
    navigateTo('video');
  }

  function goBack() {
    navigateTo('upload');
  }

  function handleScrub(e: Event) {
    const t = parseFloat((e.target as HTMLInputElement).value);
    currentTime = t;
    if (videoEl) videoEl.currentTime = t;
  }

  $effect(() => {
    return () => {
      if (session.videoUrl) URL.revokeObjectURL(session.videoUrl);
    };
  });
</script>

<div id="video-results-screen" class="screen">
  <div class="screen-header">
    <button class="btn-back" type="button" onclick={goBack}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6"/>
      </svg>
    </button>
    <div class="screen-header-text">
      <h2 class="screen-title">Analise de Video</h2>
      {#if detections.length > 0}
        <p class="screen-subtitle">
          {uniqueParts.length} pec{detections.length === 1 ? 'a' : 'as'} detectada{detections.length === 1 ? '' : 's'}
          {#if duration > 0} em {formatDuration(duration)}{/if}
        </p>
      {/if}
    </div>
  </div>

  {#if videoSrc}
    <div class="video-section">
      <div class="video-wrap">
        <video
          bind:this={videoEl}
          src={videoSrc}
          ontimeupdate={() => { if (videoEl) currentTime = videoEl.currentTime; }}
          onloadedmetadata={() => { if (videoEl) currentTime = videoEl.currentTime; }}
          crossorigin="anonymous"
          playsinline
        ></video>
      </div>

      <div class="scrubber-wrap">
        <input
          type="range"
          class="scrubber"
          min="0"
          max={duration || 0}
          step="0.1"
          value={currentTime}
          oninput={handleScrub}
        />
        <div class="markers-track" aria-hidden="true">
          {#each interesting as d}
            <button
              class="marker"
              style="left: {markerPercent(d)}%"
              title="{brandStore.translateCategory(d.className ?? '')} @ {formatDur(d.time)}"
              type="button"
              onclick={() => jumpTo(d)}
            ></button>
          {/each}
        </div>
        <div class="scrubber-labels">
          <span>{formatDur(currentTime)}</span>
          {#if duration > 0}<span>{formatDur(duration)}</span>{/if}
        </div>
      </div>
    </div>
  {/if}

  {#if interesting.length > 0}
    <div class="grid-section">
      <div class="frames-grid">
        {#each interesting as d (d.frameIndex)}
          <DetectedFrameCard
            detection={d}
            onclick={openModal}
            selected={selectedDetection?.frameIndex === d.frameIndex}
          />
        {/each}
      </div>
    </div>
  {:else}
    <div class="empty-state">
      <div class="empty-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="23 7 16 12 23 17 23 7"/>
          <rect x="1" y="5" width="15" height="14" rx="2"/>
        </svg>
      </div>
      <p class="empty-title">Nenhuma peca detectada</p>
      <p class="empty-body">Tente um video com melhor iluminacao ou mais proximo das pecas.</p>
      <button class="btn btn-process" type="button" onclick={goAnalyzeAgain}>Analisar Novamente</button>
    </div>
  {/if}

  <div class="bottom-actions">
    <button class="btn btn-process" type="button" onclick={goAnalyzeAgain}>Analisar Novamente</button>
    <button class="btn btn-outline" type="button" onclick={goBack}>Voltar</button>
  </div>
</div>

{#if selectedDetection}
  {@const d = selectedDetection}
  {@const cat = brandStore.categories.find(c => c.id === d.className)}
  {@const confClass = d.confidence >= 0.6 ? 'high' : d.confidence >= 0.4 ? 'medium' : 'low'}
  {@const confColor = confClass === 'high' ? '#22c55e' : confClass === 'medium' ? '#f59e0b' : '#ef4444'}
  <div class="modal-overlay" role="dialog" aria-modal="true" onclick={closeModal}>
    <div class="modal-card" onclick={(e) => e.stopPropagation()}>
      <div class="modal-thumb-wrap">
        <img src={d.thumbnailDataUrl} alt="" class="modal-thumb" />
        <div class="modal-thumb-overlay"></div>
        <button class="modal-close" type="button" onclick={closeModal} aria-label="Fechar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <div class="modal-cat">
          {#if cat?.icon}<span class="modal-cat-icon">{cat.icon}</span>{/if}
          <span class="modal-cat-name">{brandStore.translateCategory(d.className ?? '')}</span>
        </div>

        <div class="modal-conf-row">
          <span class="modal-conf-label">Confianca</span>
          <span class="modal-conf-pct {confClass}">{(d.confidence * 100).toFixed(0)}%</span>
        </div>
        <div class="modal-conf-bar">
          <div class="modal-conf-fill {confClass}" style="width: {d.confidence * 100}%; background: {confColor}"></div>
        </div>

        <button class="btn btn-modal-primary" type="button" onclick={goToPart}>Ver Detalhes</button>
        <button class="btn btn-modal-secondary" type="button" onclick={closeModal}>Fechar</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .screen-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
  }

  .btn-back {
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #cbd5e1;
    transition: background 0.2s;
    flex-shrink: 0;
  }

  .btn-back:hover { background: rgba(255,255,255,0.12); }

  .screen-header-text { display: flex; flex-direction: column; gap: 2px; }

  .screen-title {
    font-size: 18px;
    font-weight: 700;
    color: white;
    margin: 0;
  }

  .screen-subtitle {
    font-size: 12px;
    color: rgba(255,255,255,0.5);
    margin: 0;
  }

  .video-section { margin-bottom: 20px; }

  .video-wrap {
    border-radius: 12px;
    overflow: hidden;
    background: rgba(0,0,0,0.3);
    margin-bottom: 10px;
  }

  .video-wrap video {
    width: 100%;
    max-height: 260px;
    display: block;
  }

  .scrubber-wrap {
    position: relative;
    padding-bottom: 24px;
  }

  .scrubber {
    width: 100%;
    height: 4px;
    -webkit-appearance: none;
    appearance: none;
    background: rgba(255,255,255,0.15);
    border-radius: 2px;
    outline: none;
    cursor: pointer;
    position: relative;
    z-index: 2;
  }

  .scrubber::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #7c6ef5;
    cursor: pointer;
    box-shadow: 0 0 0 3px rgba(124,110,245,0.3);
    transition: box-shadow 0.15s;
  }

  .scrubber::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #7c6ef5;
    cursor: pointer;
    border: none;
    box-shadow: 0 0 0 3px rgba(124,110,245,0.3);
  }

  .markers-track {
    position: absolute;
    top: -4px;
    left: 0;
    right: 0;
    height: 12px;
    pointer-events: none;
    z-index: 1;
  }

  .marker {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #22c55e;
    border: 2px solid rgba(0,0,0,0.5);
    cursor: pointer;
    pointer-events: all;
    transition: transform 0.15s;
    padding: 0;
  }

  .marker:hover { transform: translate(-50%, -50%) scale(1.4); }

  .scrubber-labels {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
    font-size: 11px;
    color: rgba(255,255,255,0.4);
    font-weight: 600;
  }

  .grid-section { margin-bottom: 20px; }

  .frames-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 10px;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 24px;
    text-align: center;
    background: rgba(255,255,255,0.03);
    border: 1px dashed rgba(255,255,255,0.12);
    border-radius: 16px;
    margin-bottom: 20px;
  }

  .empty-icon { color: rgba(255,255,255,0.2); }

  .empty-title {
    font-size: 16px;
    font-weight: 700;
    color: rgba(255,255,255,0.7);
    margin: 0;
  }

  .empty-body {
    font-size: 13px;
    color: rgba(255,255,255,0.4);
    margin: 0;
    max-width: 280px;
    line-height: 1.5;
  }

  .bottom-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-weight: 700;
    font-size: 15px;
    border: none;
    border-radius: 12px;
    padding: 14px;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;
  }

  .btn-process {
    background: linear-gradient(135deg, #4338ca, #6366f1, #818cf8);
    color: white;
  }

  .btn-process:hover { filter: brightness(1.1); }
  .btn-process:active { transform: scale(0.98); }

  .btn-outline {
    background: transparent;
    color: #94a3b8;
    border: 1px solid rgba(148,163,184,0.2);
  }

  .btn-outline:hover { border-color: rgba(148,163,184,0.5); color: white; }

  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.7);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 100;
    padding: 0 0 24px;
    backdrop-filter: blur(4px);
  }

  .modal-card {
    background: #1e1e30;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 20px;
    overflow: hidden;
    width: 100%;
    max-width: 360px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.5);
  }

  .modal-thumb-wrap {
    position: relative;
    width: 100%;
    height: 200px;
  }

  .modal-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .modal-thumb-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, transparent 50%, rgba(30,30,48,0.9) 100%);
  }

  .modal-close {
    position: absolute;
    top: 12px;
    right: 12px;
    background: rgba(0,0,0,0.6);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    transition: background 0.2s;
  }

  .modal-close:hover { background: rgba(239,68,68,0.8); }

  .modal-body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .modal-cat {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .modal-cat-icon { font-size: 24px; line-height: 1; }

  .modal-cat-name {
    font-size: 20px;
    font-weight: 800;
    color: white;
  }

  .modal-conf-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-conf-label {
    font-size: 13px;
    color: rgba(255,255,255,0.5);
    font-weight: 600;
  }

  .modal-conf-pct {
    font-size: 16px;
    font-weight: 800;
  }

  .modal-conf-pct.high { color: #22c55e; }
  .modal-conf-pct.medium { color: #f59e0b; }
  .modal-conf-pct.low { color: #ef4444; }

  .modal-conf-bar {
    height: 8px;
    background: rgba(255,255,255,0.1);
    border-radius: 4px;
    overflow: hidden;
  }

  .modal-conf-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  .btn-modal-primary {
    background: linear-gradient(135deg, #4338ca, #6366f1);
    color: white;
    font-weight: 700;
    font-size: 15px;
    border: none;
    border-radius: 12px;
    padding: 13px;
    cursor: pointer;
    transition: filter 0.2s;
  }

  .btn-modal-primary:hover { filter: brightness(1.1); }

  .btn-modal-secondary {
    background: transparent;
    color: #94a3b8;
    font-weight: 600;
    font-size: 14px;
    border: 1px solid rgba(148,163,184,0.2);
    border-radius: 10px;
    padding: 10px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-modal-secondary:hover { border-color: rgba(148,163,184,0.5); color: white; }
</style>
